import React, { useState } from 'react';
import { Card, notification } from 'antd';
import OnboardingStartScreen from './OnboardingStartScreen';
import AddNewWebsiteForm from './AddNewWebsiteForm';
import DomainSetupProgress from './DomainSetupProgress';
import { useCreateDomainSetupProcessMutation } from '../../process/service';
import { AddNewWebsiteIndexEntryParams } from '../types';


import { useAddTrackedKeywordsMutation } from '../../../trackedKeywords/service';
import { useAddWebsiteIndexEntryMutation } from '../service';
import { useAddSitemapImportProcessMutation, useAddDomainAnalysisProcessMutation } from '../..//process/service';
import { useCreateBackgroundAnalysisReportMutation } from '../../../analysis/service';
import { useSendOnboardingEventMutation } from '../../../trackedWebsites/service';
import useGetCurrentWebsite from '../hooks/useGetCurrentWebsite';
import './AddNewWebsite.less';
import { useSendLoadingTimeRequestMutation } from '../../../siteSpeed/service';


type Props = {
    fromOnboarding: boolean;
    onFinish: () => void;
};


export default function AddNewWebsiteIndexEntry({ fromOnboarding, onFinish }: Props) {
    const [inputDomain, setInputDomain] = useState<string | null>(null);
    const [createDomainSetupProcess] = useCreateDomainSetupProcessMutation();


    const { setSelectedWebsite } = useGetCurrentWebsite();
    const [addWebsiteIndexEntry] = useAddWebsiteIndexEntryMutation();
    const [addTrackedKeywords] = useAddTrackedKeywordsMutation();
    const [addSitemapImportProcess] = useAddSitemapImportProcessMutation();
    const [addDomainAnalysisProcess] = useAddDomainAnalysisProcessMutation();
    const [createBackgroundAnalysis] = useCreateBackgroundAnalysisReportMutation();
    const [sendOnboardingEvent] = useSendOnboardingEventMutation();
    const [sendLoadingTimeRequest] = useSendLoadingTimeRequestMutation();

    const onValidDomain = (domain: string) => {
        createDomainSetupProcess({ domain })
            .unwrap()
            .then(() => {
                setInputDomain(domain);
            })
            .catch((error) => {
                console.error("Failed to create domain setup process:", error);
            });
    };

    const handleAddNewWebsite = async (params: AddNewWebsiteIndexEntryParams, keywords: string[]) => {
        try {
            let newWebsiteIndexEntry;

            try {
                newWebsiteIndexEntry = await addWebsiteIndexEntry({ domain: params.domain, homepage_url: params.homepage, settings: params.settings }).unwrap();
            } catch (error) {
                notification.error({
                    message: "Something went wrong!",
                    description: `There was an error adding ${params.domain}. Please try again or contact support!`,
                });
                return;
            }

            try {
                // send loading time request
                await sendLoadingTimeRequest({ url: params.homepage, regions: params.settings.regions }).unwrap();
            } catch (err) {
                console.error("Failed to send loading time request:", err);
            }

            if (newWebsiteIndexEntry) {
                try {
                    // start sitemap import
                    if (newWebsiteIndexEntry.sitemap_urls.length > 0) {
                        await addSitemapImportProcess({ w_guid: newWebsiteIndexEntry.guid, sitemap_urls: newWebsiteIndexEntry.sitemap_urls }).unwrap();
                    }

                    // start site-wide factors analysis
                    await addDomainAnalysisProcess({ w_guid: newWebsiteIndexEntry.guid }).unwrap();

                    // start a full legacy seo-audit
                    const source = fromOnboarding ? "onboarding_wizard" : "add_tracked_website_wizard";
                    const backgroundAnalysisReport = await createBackgroundAnalysis({ url: params.homepage, competitors: params.settings.competitors, source }).unwrap();

                    // send finish_onboarding tracking event only if background analysis was initiated from the onboarding wizard
                    if (fromOnboarding) {
                        const onboardingEvent = {
                            main_url: params.homepage,
                            report_guid: backgroundAnalysisReport.guid,
                            location_name: params.settings.location_name,
                            language_code: params.settings.language_code,
                        };

                        await sendOnboardingEvent({ event_type: "finish_onboarding", meta: onboardingEvent }).unwrap();
                    }

                    // add tracked keywords
                    await addTrackedKeywords({
                        domain: params.domain,
                        locationName: params.settings.location_name,
                        languageCode: params.settings.language_code,
                        keywords: keywords,
                    }).unwrap();

                    setSelectedWebsite(newWebsiteIndexEntry.guid);
                } catch (_) {
                    notification.error({ message: "Something went wrong!", description: "Please try again or contact support!" });
                }
            }
        } catch (error) {
        } finally {
            onFinish();
        }
    };

    return (
        <Card bordered={false} className="add-website-index-entry-card">
            {
                inputDomain === null ? (
                    fromOnboarding ? <OnboardingStartScreen onValidDomain={onValidDomain} /> : <AddNewWebsiteForm onValidDomain={onValidDomain} title={"Add a new website to start SEO monitoring.<br /> After setup, we'll start a <strong>full SEO analysis</strong> to identify issues and deliver clear <strong>how-to-fix</strong> guidance."} />
                ) : <DomainSetupProgress domain={inputDomain} onSetupFinish={handleAddNewWebsite} />
            }
        </Card>
    );
}
